import React from "react";
import PageCard from "features/shared/components/card/PageCard";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import ContactPersonDTO from "features/contractualAccounts/dtos/ContactPersonDTO";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import ContactDTO from "features/customer/dtos/ContactDTO";
import { Nullable } from "features/shared/types/nullable";
import Alert from "features/shared/alerts/components/Alert";
import { useTranslation } from "react-i18next";
import { ContactPersonType } from "features/contractualAccounts/enums/contactPersonType";
import FormGroup from "features/shared/forms/components/FormGroup";

type Props = {
  persons: ContactPersonDTO[];
  formHandler: UseFormHookResult;
};

const PersonsInRelationshipSubForm = ({ persons, formHandler }: Props) => {
  // translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // form hook props
  const { bindCheckField, errors } = { ...formHandler };

  // helpers
  const getContacts = (contacts: ContactDTO[]): Nullable<string> => {
    const email = contacts.find((x) => x.contactType === "Email");
    const mobile = contacts.find((x) => x.contactType === "Mobile");

    if (email && mobile) {
      return `${email.value} | ${mobile.value}`;
    }

    return null;
  };

  const getPersons = (personType: ContactPersonType) => {
    return persons.filter((x) => x.contactPersonType === personType);
  };

  // sub-renders
  const renderPersons = (
    contactPersons: ContactPersonDTO[],
    bindingName: string
  ) => {
    return contactPersons.map((person) => {
      const contacts = getContacts(person.contacts!);

      return (
        <div className="col-lg-6 col-xl-4 px-2" key={person.contactPersonId}>
          <RadioBox
            label={person.contactPersonName!}
            text={
              contacts ?? tLabels("personsInRelationshipSubForm.noContacts")
            }
            icon={{ icon: "user" }}
            {...bindCheckField(bindingName, person.contactPersonId)}
            disabled={contacts === null}
          />
        </div>
      );
    });
  };

  const renderContactPersons = (
    contactPersons: ContactPersonDTO[],
    bindingName: string
  ) => {
    if (contactPersons.length > 0) {
      return (
        <>
          <div className="row no-gutters mx-n2">
            {renderPersons(contactPersons, bindingName)}
          </div>
          <p className="text-muted small font-italic mb-2">
            {tLabels("personsInRelationshipSubForm.contactPersonsSelectInfo")}
          </p>
        </>
      );
    } else {
      return (
        <p className="text-muted font-italic mb-2">
          {tLabels("personsInRelationshipSubForm.contactPersonsNotFound")}
        </p>
      );
    }
  };

  // render
  return (
    <>
      <Alert
        alertObj={{
          alertType: "Info",
          message: tLabels("personsInRelationshipSubForm.selectPersonsInfo"),
        }}
        className="mb-4"
      />
      <PageCard
        title={tLabels(
          "personsInRelationshipSubForm.authorizedPersonCardTitle"
        )}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <FormGroup
          validationError={errors["authorizedPersonId"]}
          className="mb-0"
        >
          <div className="row no-gutters mx-n2">
            {renderPersons(
              getPersons("PersonAuthorizedToSign"),
              "authorizedPersonId"
            )}
          </div>
        </FormGroup>
      </PageCard>
      <PageCard
        title={tLabels("personsInRelationshipSubForm.contactPersonCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        {renderContactPersons(getPersons("ContactPerson"), "contactPersonId")}
      </PageCard>
    </>
  );
};

export default PersonsInRelationshipSubForm;
